import * as React from "react";
import { MobXProviderContext, observer } from "mobx-react";
import { updateSearch } from "bernie-core";
import { useLocation, useHistory } from "react-router-dom";

import {
  UitkExperimentalButtonTabs,
  UitkTab as UitkExperimentalTab,
  UitkTabAnchorType as UitkTabExperimentalAnchorType,
  UitkTabsType as UitkExperimentalTabsType,
} from "uitk-react-experimental-button-tabs";
import { UitkTab, UitkTabs, UitkTabsType } from "uitk-react-tabs";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkSpacingProps } from "uitk-react-spacing/dist/uitk-spacing";

import { useLocalization } from "@shared-ui/localization-context";
import { ViewMedium, Viewport, ViewSmall } from "@shared-ui/viewport-context";

import { withStores } from "stores";
import { ExternalLinkTabConfig, Navigation } from "stores/wizard/config";
import { ClientWizardStateStore } from "stores/wizard/ClientWizardStateStore";
import { FlexModuleModelStore } from "stores/FlexModuleModelStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Experiment as Experiments, isVariantEnabled } from "components/utility/experiment";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";

import { TabbedWizard } from "./TabbedWizard";
import { getGTLabelTokenFromState, getLobConfigFromState } from "./ConfigHelpers/LobsConfig";
import { getExternalLinkTabConfig, getExternalLinkTabURL, isExtLinkTab } from "./ConfigHelpers/ExtLinkConfig";
import { LOB_ICONS_MAP, LOB_KEYS, wizardLobQueryParamKey } from "../constants";

/**
 * @property lobForms: a list of React elements representing each LOB form added in the flex manager
 */
export interface LobFormWithTabsProps {
  lobForms: React.ReactElement[];
  wizardFormClassNames?: string;
  wizardFormSpacing?: UitkSpacingProps;
  tabGroupClassNames?: string;
  trackOnChangeTab?: (tabName: string, rfrrId?: string) => void;
  context?: ExtendedContextStore;
  flexModuleModelStore: FlexModuleModelStore;
  showTabsIcon?: boolean;
}

const { BEX_Homepage_Tabs_Test } = Experiments;

export const LobFormWithTabs = withStores("flexModuleModelStore")(
  observer((props: LobFormWithTabsProps) => {
    const {
      lobForms,
      wizardFormClassNames,
      wizardFormSpacing,
      tabGroupClassNames,
      trackOnChangeTab,
      context,
      flexModuleModelStore,
      showTabsIcon = true,
    } = props;
    const keyHelperWizard = new ItemKeyHelper("storefrontForms");
    const keyHelperNavigation = new ItemKeyHelper("storefrontWizardNavigation");
    const wizardState: ClientWizardStateStore = React.useContext(MobXProviderContext).wizardState;
    const [isModuleModelStoreInitialized, setModuleModelStoreInitialized] = React.useState(false);
    const defaultLobNavConfig: Navigation = {
      tabIcon: "help",
      tabTitleToken: "",
    };
    const location = useLocation();
    const history = useHistory();
    const queryParams = new URLSearchParams(location.search);

    const { formatText } = useLocalization();

    /*
     * Temporary workaround to initialize wizard modules on server side rendering
     * from the flexModuleModelStore in the modelMap Map property.
     * We can remove this when the flex template renderer is refactored 100% in blossom.
     * Details of this implementation on this confluence page:
     * https://confluence.expedia.biz/display/POS/Flex+template+renderer+react+refactor+under+Wizard.
     */
    if (!isModuleModelStoreInitialized) {
      flexModuleModelStore.getModel("wizard-flight-pwa-1");
      flexModuleModelStore.getModel("wizard-hotel-pwa-v2-1");
      flexModuleModelStore.getModel("wizard-cruise-pwa-1");
      flexModuleModelStore.getModel("wizard-package-pwa-1");
      flexModuleModelStore.getModel("wizard-thirdPartyPackage-pwa-1");
      flexModuleModelStore.getModel("wizard-car-pwa-1");
      flexModuleModelStore.getModel("wizard-vr-pwa-1");
      flexModuleModelStore.getModel("wizard-lx-pwa-1");
      flexModuleModelStore.getModel("wizard-hotel-pwa-1");
      flexModuleModelStore.getModel("wizard-externalLinkTab-pwa-1");
      setModuleModelStoreInitialized(true);
    }

    //returns the localized string given the name of the flex component used
    const getLabel = (lobName: string): string => {
      let label = defaultLobNavConfig.tabTitleToken;
      const lobStateConfig = getLobConfigFromState(lobName, wizardState);
      if (lobStateConfig) {
        label = formatText(lobStateConfig?.navigation?.tabTitleToken);
      }
      if (LOB_KEYS.GT === lobName) {
        label = formatText(getGTLabelTokenFromState(wizardState));
      }

      return label;
    };

    //set the state to hold the default lob in the wizard store as the current value
    const [currentExperimentalTab, setCurrentExperimentalTab] = React.useState<string>(getLabel(wizardState.activeLob));
    const defaultTabIndex = lobForms.map((lob) => lob.props.flexComponent.name).indexOf(wizardState.activeLob);
    const [currentTabIndexLob, setCurrentTabIndexLob] = React.useState(defaultTabIndex);

    const renderExperimentalTabs = !isVariantEnabled(context, BEX_Homepage_Tabs_Test);

    /**
     * Check the route store, if there is no LOB query attached, get it from the wizardState default and redirect
     * CurrentLOB and defaultLOB set server side, but may be updated based on last search cookie value client side
     * To ensure up to date values on client side render, trigger an effect that does not have any dependent values to only run on first render
     */
    React.useEffect(() => {
      if (!queryParams.get("pwaLob")) {
        const { useDefaultLOB, defaultLob: pwaLob } = wizardState;
        if (useDefaultLOB) {
          wizardState.setUseDefaultLOB(false);
          if (renderExperimentalTabs) {
            setCurrentExperimentalTab(getLabel(pwaLob));
          } else {
            const index = lobForms.map((lob) => lob.props.flexComponent.name).indexOf(pwaLob);
            setCurrentTabIndexLob(index);
          }
        }
      }
    }, []);

    /**
     * Map localized form label to flex component name
     */
    const LobFormNames: { [label: string]: string } = {};
    lobForms.map((lob: React.ReactElement) => {
      const { name, id } = lob?.props?.flexComponent;

      if (isExtLinkTab(name) && id) {
        LobFormNames[id] = name;
      } else {
        const formLabel = getLabel(name);
        LobFormNames[formLabel] = name;
      }
    });

    const getFormNameFromLabel = (label: string): string => {
      return LobFormNames[label];
    };

    const cleanupPwaLobQueryParam = () => {
      updateSearch({ history, location, newParams: { pwaLob: "" } });
    };

    const externalLinkTabs = lobForms.filter((lob) => lob?.key?.toString().includes(LOB_KEYS.EXT_LINK));

    const onExperimentalTabSelect = (tabSelected: string) => {
      if (queryParams.get("pwaLob")) {
        cleanupPwaLobQueryParam();
      }

      const formName = getFormNameFromLabel(tabSelected);
      if (!isExtLinkTab(formName)) {
        setGlobalValuesOnTabChange(formName);
        setCurrentExperimentalTab(tabSelected);
      } else {
        redirectTo(formName, tabSelected);
      }
    };

    const onTabSelect = (tabIndexSelected: number) => {
      if (queryParams.get("pwaLob")) {
        cleanupPwaLobQueryParam();
      }

      const { name, id } = lobForms[tabIndexSelected].props.flexComponent;

      const tabNameSelected = getLabel(name);
      const formName = getFormNameFromLabel(tabNameSelected) || getFormNameFromLabel(id);

      if (!isExtLinkTab(formName)) {
        setGlobalValuesOnTabChange(formName);
        setCurrentTabIndexLob(tabIndexSelected);
      } else {
        redirectTo(formName, id);
      }
    };

    /**
     * Track (if available) and set wizard state
     */
    const setGlobalValuesOnTabChange = (formName: string) => {
      if (trackOnChangeTab) {
        trackOnChangeTab(formName);
      }
      shouldUpdateLOBCookie(false);
      wizardState.setActiveLob(formName);

      resetErrorsState();
    };

    /**
     * Redirect to another page when tab is used as link
     */
    const redirectTo = (formName: string, tabNameSelected: string) => {
      const currentLob = externalLinkTabs.find((lob) => lob.key === tabNameSelected);
      const rfrrId = currentLob?.props.flexComponent.model.rfrrId;
      const externalLinkTabURL = getExternalLinkTabURL(formName, context, null, currentLob, wizardState);
      if (externalLinkTabURL) {
        window.open(externalLinkTabURL);
      }
      if (trackOnChangeTab) {
        trackOnChangeTab(formName, rfrrId);
      }
    };

    const shouldUpdateLOBCookie = (shouldUpdate: boolean) => {
      wizardState.globalWizardState.updateShouldUpdateFromCookie(shouldUpdate);
    };

    const resetErrorsState = () => {
      wizardState.carWizardState.resetSubLobState();
      wizardState.carWizardState.resetSubLobTabs();
      wizardState.carWizardState.cleanErrorState();

      wizardState.lxWizardState.cleanErrorState();
      wizardState.hotelWizardState.cleanErrorState();
      wizardState.flightWizardState.cleanErrorState();
      wizardState.packageWizardState.cleanErrorState();
    };

    const tabsContent = lobForms.map((lob: React.ReactElement, index) => {
      const { name, id } = lob.props.flexComponent;
      const iconKey = showTabsIcon ? LOB_ICONS_MAP[name] : "";
      const label = getLabel(name);
      const defaultTabProps = {
        "data-testid": label,
        name: label,
        targetURI: `?${wizardLobQueryParamKey}=${name}`,
        id: name,
        iconKey,
        tabLinkAttr: { rel: "nofollow" },
      };

      if (isExtLinkTab(name)) {
        // wizard-externalLinkTab-pwa module only
        // renders only a tab without any content or form
        const externalLinkConfig: ExternalLinkTabConfig | null = getExternalLinkTabConfig(lob, wizardState);
        const { tabIcon, tabTitleToken } = externalLinkConfig?.navigation || {
          tabIcon: iconKey,
          tabTitleToken: null,
        };
        const extLinkTabProps = {
          ...defaultTabProps,
          displayName: tabTitleToken ? formatText(tabTitleToken) : undefined,
          name: id,
          targetURI: getExternalLinkTabURL(name, context, externalLinkConfig) || undefined,
          id,
          iconKey: tabIcon,
        };

        if (renderExperimentalTabs) {
          return <UitkExperimentalTab {...extLinkTabProps} key={keyHelperWizard.next()} />;
        }

        return <UitkTab {...defaultTabProps} key={keyHelperNavigation.next()} />;
      }

      if (renderExperimentalTabs) {
        return (
          <UitkExperimentalTab {...defaultTabProps} key={keyHelperNavigation.next()}>
            {currentExperimentalTab === label && (
              <UitkSpacing {...wizardFormSpacing}>
                <div className={wizardFormClassNames || ""}>
                  <TabbedWizard key={keyHelperWizard.next()}>{lob}</TabbedWizard>
                </div>
              </UitkSpacing>
            )}
          </UitkExperimentalTab>
        );
      }

      return (
        <UitkTab {...defaultTabProps} key={keyHelperNavigation.next()}>
          {currentTabIndexLob === index && (
            <UitkSpacing {...wizardFormSpacing}>
              <div className={wizardFormClassNames || ""}>
                <TabbedWizard key={keyHelperWizard.next()}>{lob}</TabbedWizard>
              </div>
            </UitkSpacing>
          )}
        </UitkTab>
      );
    });

    const tabsProps = {
      className: `lobNavigationFormWithTabs ${tabGroupClassNames || ""}`,
      "data-testid": "lobNavigationFormWithTabs",
    };

    if (renderExperimentalTabs) {
      return (
        <UitkExperimentalButtonTabs
          {...tabsProps}
          onTabSelect={onExperimentalTabSelect}
          selectedTab={currentExperimentalTab}
          tabsType={showTabsIcon ? UitkExperimentalTabsType.NATURAL : UitkExperimentalTabsType.EQUAL_WIDTH}
          tabAnchorType={
            showTabsIcon ? UitkTabExperimentalAnchorType.ICON_AND_TEXT : UitkTabExperimentalAnchorType.DEFAULT
          }
        >
          {tabsContent}
        </UitkExperimentalButtonTabs>
      );
    }

    return (
      <Viewport>
        <ViewSmall>
          <UitkSpacing margin={{ blockstart: "two" }}>
            <UitkTabs
              {...tabsProps}
              onTabSelect={onTabSelect}
              selectedTab={currentTabIndexLob}
              tabsType={UitkTabsType.NATURAL}
            >
              {tabsContent}
            </UitkTabs>
          </UitkSpacing>
        </ViewSmall>
        <ViewMedium>
          <UitkSpacing margin={{ blockstart: "three" }}>
            <UitkTabs
              {...tabsProps}
              onTabSelect={onTabSelect}
              centerAlign
              selectedTab={currentTabIndexLob}
              tabsType={UitkTabsType.NATURAL}
            >
              {tabsContent}
            </UitkTabs>
          </UitkSpacing>
        </ViewMedium>
      </Viewport>
    );
  })
);
